import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import DashboardChartCardKebabMenu from "./DashboardChartCardKebabMenu";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { mergeDepositModel } from "./dashboardChartsModel";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import "./dashboardChartJsSetup";

const COLOR_AMOUNT = "#5CC7C1";
const COLOR_TRANSACTION = "#F9BC44";

/** Integer-only max + step for transaction (left) axis. */
function txnAxisIntegerMaxAndStep(maxTxn, tickTarget = 6) {
  const m = Math.max(0, Number(maxTxn) || 0);
  const padded = Math.max(1, Math.ceil(m * 1.25));
  const naive = Math.ceil(padded / Math.max(2, tickTarget - 1));
  let step = Math.max(1, naive);
  const nice = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000];
  const hit = nice.find((n) => n >= naive);
  if (hit != null) step = hit;
  const axisMax = Math.max(padded, Math.ceil(padded / step) * step);
  return { max: axisMax, stepSize: step };
}

/** Right-axis max/step for amount (positive integers, k-style ticks). */
function amountAxisMaxStep(maxAmount) {
  const m = Math.max(0, Number(maxAmount) || 0);
  const padded = Math.max(1000, Math.ceil(m * 1.15));
  const candidates = [500, 1000, 2000, 5000, 10000, 20000, 25000, 50000, 100000, 200000, 250000, 500000, 1000000];
  const step = candidates.find((s) => s >= padded / 5) ?? Math.ceil(padded / 5);
  const axisMax = Math.max(step, Math.ceil(padded / step) * step);
  return { max: axisMax, stepSize: step };
}

function formatInt(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(n));
}

function formatTransactionTick(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  if (Math.abs(n - Math.round(n)) < 1e-6) return String(Math.round(n));
  return n.toFixed(1);
}

function formatAmountTick(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  if (Math.abs(n) >= 1000) return `${Math.round(n / 1000)}k`;
  return String(Math.round(n));
}

function DepositChartBody({ rows, totalAmount, totalTransactions, avgAmount }) {
  const { t } = useTranslation();
  const { chartData, options } = useMemo(() => {
    const labels = rows.map((row) => row.date);
    const amount = rows.map((row) => row.amount);
    const transaction = rows.map((row) => row.transaction);
    const n = labels.length;
    const maxT = Math.max(0, ...transaction, 0);
    const { max: txnMax, stepSize: txnStep } = txnAxisIntegerMaxAndStep(maxT);
    const maxA = Math.max(0, ...amount, 0);
    const { max: amtMax, stepSize: amtStep } = amountAxisMaxStep(maxA);

    return {
      chartData: {
        labels,
        datasets: [
          {
            type: "bar",
            label: t("Amount", { defaultValue: "Amount" }),
            data: amount,
            backgroundColor: COLOR_AMOUNT,
            borderRadius: 0,
            borderSkipped: false,
            maxBarThickness: 26,
            yAxisID: "yAmount",
            order: 1,
          },
          {
            type: "line",
            label: t("Transaction", { defaultValue: "Transaction" }),
            data: transaction,
            borderColor: COLOR_TRANSACTION,
            backgroundColor: COLOR_TRANSACTION,
            pointBackgroundColor: COLOR_TRANSACTION,
            pointBorderColor: COLOR_TRANSACTION,
            pointRadius: 4,
            pointHoverRadius: 4,
            borderWidth: 2,
            tension: 0,
            yAxisID: "yTxn",
            order: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              usePointStyle: true,
              color: "#2A2A2A",
              font: { family: "'Segoe UI', Roboto, sans-serif", size: 12 },
            },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                if (ctx.datasetIndex === 0) {
                  return ` ${t("Amount", { defaultValue: "Amount" })}: ${formatInt(ctx.parsed?.y ?? 0)}`;
                }
                return ` ${t("Transaction", { defaultValue: "Transaction" })}: ${formatInt(ctx.parsed?.y ?? 0)}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: "#666666",
              font: { family: "Roboto, 'Segoe UI', sans-serif", size: 11 },
              maxRotation: n > 7 ? 35 : 0,
              minRotation: 0,
            },
            border: { display: false },
          },
          yTxn: {
            type: "linear",
            position: "left",
            min: 0,
            max: txnMax,
            ticks: {
              stepSize: txnStep,
              color: "#666666",
              font: { family: "Roboto, 'Segoe UI', sans-serif", size: 11 },
              callback: (v) => formatTransactionTick(v),
            },
            title: {
              display: true,
              text: t("Transaction", { defaultValue: "Transaction" }),
              color: "#666666",
              font: { family: "Roboto, 'Segoe UI', sans-serif", size: 12, weight: "400" },
            },
            grid: {
              color: "#E6E6E6",
              drawTicks: false,
            },
            border: { display: false },
          },
          yAmount: {
            type: "linear",
            position: "right",
            min: 0,
            max: amtMax,
            ticks: {
              stepSize: amtStep,
              color: "#666666",
              font: { family: "Roboto, 'Segoe UI', sans-serif", size: 11 },
              callback: (v) => formatAmountTick(v),
            },
            title: {
              display: true,
              text: t("Amount", { defaultValue: "Amount" }),
              color: "#666666",
              font: { family: "Roboto, 'Segoe UI', sans-serif", size: 12, weight: "400" },
            },
            grid: {
              drawOnChartArea: false,
              drawTicks: false,
            },
            border: { display: false },
          },
        },
      },
    };
  }, [rows, t]);

  return (
    <div className="pos-deposit-chart">
      <div className="pos-deposit-chart__kpis">
        <div className="pos-deposit-chart__kpi">
          <span className="pos-deposit-chart__kpi-label">{t("Total Amount", { defaultValue: "Total Amount" })}</span>
          <span className="pos-deposit-chart__kpi-value">{formatInt(totalAmount)}</span>
        </div>
        <div className="pos-deposit-chart__kpi">
          <span className="pos-deposit-chart__kpi-label">{t("Transaction", { defaultValue: "Transaction" })}</span>
          <span className="pos-deposit-chart__kpi-value">{formatInt(totalTransactions)}</span>
        </div>
        <div className="pos-deposit-chart__kpi">
          <span className="pos-deposit-chart__kpi-label">{t("Avg.", { defaultValue: "Avg." })}</span>
          <span className="pos-deposit-chart__kpi-value">{formatInt(avgAmount)}</span>
        </div>
      </div>
      <div className="pos-deposit-chart__plot">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
}

function DepositTableBody({ rows, totalAmount, totalTransactions, avgAmount }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState(() => ({
    key: /** @type {"date"|"transaction"|"amount"|"avg"} */ ("date"),
    dir: /** @type {"asc"|"desc"} */ ("asc"),
  }));

  useEffect(() => {
    setSort({ key: "date", dir: "asc" });
  }, [rows]);

  const sortedRows = useMemo(() => {
    const indexed = rows.map((row, i) => ({
      ...row,
      avg: row.transaction > 0 ? row.amount / row.transaction : 0,
      _index: i,
    }));
    const mul = sort.dir === "asc" ? 1 : -1;
    indexed.sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case "date":
          cmp = String(a.date).localeCompare(String(b.date), undefined, { sensitivity: "base" });
          break;
        case "transaction":
          cmp = a.transaction - b.transaction;
          break;
        case "amount":
          cmp = a.amount - b.amount;
          break;
        case "avg":
          cmp = a.avg - b.avg;
          break;
        default:
          cmp = 0;
      }
      if (cmp !== 0) return cmp * mul;
      return a._index - b._index;
    });
    return indexed;
  }, [rows, sort]);

  const toggleSort = useCallback((key) => {
    setSort((current) =>
      current.key !== key ? { key, dir: "asc" } : { key, dir: current.dir === "asc" ? "desc" : "asc" },
    );
  }, []);

  const thBase = "pos-deposit-table__th";
  const thSorted = "pos-deposit-table__th--sorted";

  return (
    <div className="pos-deposit-table" role="table" aria-label={t("Deposit", { defaultValue: "Deposit" })}>
      <div className="pos-deposit-table__head" role="rowgroup">
        <div className="pos-deposit-table__tr pos-deposit-table__tr--head" role="row">
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-deposit-table__th--date"
            ariaSortLabel={t("Sort by date", { defaultValue: "Sort by date" })}
            sorted={sort.key === "date"}
            direction={sort.dir}
            onSort={() => toggleSort("date")}
          >
            {t("Date", { defaultValue: "Date" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-deposit-table__th--num"
            ariaSortLabel={t("Sort by transaction", { defaultValue: "Sort by transaction" })}
            sorted={sort.key === "transaction"}
            direction={sort.dir}
            onSort={() => toggleSort("transaction")}
          >
            {t("Transaction", { defaultValue: "Transaction" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-deposit-table__th--num"
            ariaSortLabel={t("Sort by amount", { defaultValue: "Sort by amount" })}
            sorted={sort.key === "amount"}
            direction={sort.dir}
            onSort={() => toggleSort("amount")}
          >
            {t("Amount", { defaultValue: "Amount" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-deposit-table__th--num pos-deposit-table__th--last"
            ariaSortLabel={t("Sort by average", { defaultValue: "Sort by average" })}
            sorted={sort.key === "avg"}
            direction={sort.dir}
            onSort={() => toggleSort("avg")}
          >
            {t("Avg.", { defaultValue: "Avg." })}
          </DashboardChartTableSortHeader>
        </div>
      </div>
      <div className="pos-deposit-table__scroll" role="rowgroup">
        {sortedRows.map((row) => {
          return (
            <div key={`${row.date}-${row._index}`} className="pos-deposit-table__tr" role="row">
              <div className="pos-deposit-table__td pos-deposit-table__td--date" role="cell">
                {row.date}
              </div>
              <div className="pos-deposit-table__td pos-deposit-table__td--num" role="cell">
                {formatInt(row.transaction)}
              </div>
              <div className="pos-deposit-table__td pos-deposit-table__td--num" role="cell">
                {formatInt(row.amount)}
              </div>
              <div className="pos-deposit-table__td pos-deposit-table__td--num" role="cell">
                {formatInt(row.avg)}
              </div>
            </div>
          );
        })}
        <div className="pos-deposit-table__tr pos-deposit-table__tr--total" role="row">
          <div className="pos-deposit-table__td pos-deposit-table__td--date" role="cell">
            {t("Total", { defaultValue: "Total" })}
          </div>
          <div className="pos-deposit-table__td pos-deposit-table__td--num" role="cell">
            {formatInt(totalTransactions)}
          </div>
          <div className="pos-deposit-table__td pos-deposit-table__td--num" role="cell">
            {formatInt(totalAmount)}
          </div>
          <div className="pos-deposit-table__td pos-deposit-table__td--num" role="cell">
            {formatInt(avgAmount)}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Deposit card (chart + table views).
 *
 * @param {object} props
 * @param {import("./dashboardChartsModel.js").DepositChartModel | undefined} [props.model]
 * @param {() => void} [props.onMenuRemove]
 */
function DepositChartCard({ model, onMenuRemove }) {
  const { t } = useTranslation();
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [viewMode, setViewMode] = useState(/** @type {"chart" | "table"} */ ("chart"));

  const merged = useMemo(() => mergeDepositModel(model && typeof model === "object" ? model : null), [model]);

  const { rows, totalAmount, totalTransactions, avgAmount } = merged;

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("deposit"));
  }, []);

  return (
    <div className={`pos-dashboard-chart-card pos-dashboard-chart-card--deposit${viewMode === "table" ? " pos-dashboard-chart-card--deposit-table" : ""}`}>
      <header className="pos-dashboard-chart-card__header">
        <h2 className="pos-dashboard-chart-card__title">{t("Deposit", { defaultValue: "Deposit" })}</h2>
        <DashboardChartCardKebabMenu
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onDownload={handleDownloadPng}
          onRemove={onMenuRemove}
          showViewToggle
        />
      </header>
      <div className={`pos-dashboard-chart-card__body pos-dashboard-chart-card__body--deposit${viewMode === "table" ? " pos-dashboard-chart-card__body--deposit-table" : ""}`}>
        <div ref={captureRef} className="pos-dashboard-chart-card__capture pos-dashboard-chart-card__capture--deposit">
          {viewMode === "chart" ? (
            <DepositChartBody
              rows={rows}
              totalAmount={totalAmount}
              totalTransactions={totalTransactions}
              avgAmount={avgAmount}
            />
          ) : null}
          {viewMode === "table" ? (
            <DepositTableBody
              rows={rows}
              totalAmount={totalAmount}
              totalTransactions={totalTransactions}
              avgAmount={avgAmount}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default DepositChartCard;
